import React, { useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import SetupScreen from './components/SetupScreen';
import InterviewScreen from './components/InterviewScreen';
import FeedbackScreen from './components/FeedbackScreen';
import ChloeModeOverlay from './components/ChloeModeOverlay';

const defaultTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#6366f1' },
    secondary: { main: '#10b981' },
    background: { default: '#0f172a', paper: '#1e293b' },
    text: { primary: '#f1f5f9', secondary: '#94a3b8' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
  },
});

const chloeTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e91e8c' },
    secondary: { main: '#ff6fb0' },
    background: { default: '#fce4ec', paper: '#ffd6e7' },
    text: { primary: '#880e4f', secondary: '#c2185b' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
  },
});

export default function App() {
  const [screen, setScreen] = useState('setup'); // 'setup' | 'interview' | 'feedback'
  const [profile, setProfile] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [chloeMode, setChloeMode] = useState(false);

  function handleSetupComplete(profileData) {
    setProfile(profileData);
    setScreen('interview');
  }

  function handleInterviewEnd(transcriptData) {
    setTranscript(transcriptData);
    setScreen('feedback');
  }

  function handleFeedbackReceived(feedbackData) {
    setFeedback(feedbackData);
  }

  function handleRestart() {
    setProfile(null);
    setTranscript([]);
    setFeedback(null);
    setScreen('setup');
  }

  return (
    <ThemeProvider theme={chloeMode ? chloeTheme : defaultTheme}>
      <CssBaseline />
      {chloeMode && <ChloeModeOverlay />}
      {/* Chloe Mode toggle — fixed top-right */}
      <Box
        onClick={() => setChloeMode(m => !m)}
        sx={{
          position: 'fixed',
          top: 12,
          right: 16,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          px: 1.5,
          py: 0.75,
          borderRadius: '20px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: '"Inter", sans-serif',
          background: chloeMode ? 'rgba(233,30,140,0.15)' : 'rgba(255,255,255,0.08)',
          border: chloeMode ? '2px solid #e91e8c' : '2px solid rgba(255,255,255,0.15)',
          color: chloeMode ? '#e91e8c' : 'inherit',
          transition: 'all 0.3s',
          userSelect: 'none',
          '&:hover': { background: 'rgba(233,30,140,0.25)', borderColor: '#e91e8c' },
        }}
      >
        <span>🐾</span>
        <span>Chloe Mode</span>
      </Box>
      {screen === 'setup' && (
        <SetupScreen onComplete={handleSetupComplete} />
      )}
      {screen === 'interview' && profile && (
        <InterviewScreen
          profile={profile}
          onEnd={handleInterviewEnd}
        />
      )}
      {screen === 'feedback' && (
        <FeedbackScreen
          profile={profile}
          transcript={transcript}
          feedback={feedback}
          onFeedbackReceived={handleFeedbackReceived}
          onRestart={handleRestart}
        />
      )}
    </ThemeProvider>
  );
}
